import { useEffect, useState, useRef } from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export interface UserProfile {
  id: string
  name: string
  email: string
  business_name?: string
  phone?: string
  avatar_url?: string
  email_mode?: string
  is_admin: boolean
  referral_code?: string
  referred_by?: string
  created_at: string
  updated_at: string
}

const profileCache = new Map<string, { data: UserProfile; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000;
const PROFILE_SELECT = 'id, name, email, business_name, phone, avatar_url, is_admin, referral_code, referred_by, created_at, updated_at';

const welcomeBonusClaimed = new Set<string>();

async function claimWelcomeBonusForNewUser(userId: string): Promise<void> {
  if (welcomeBonusClaimed.has(userId)) {
    return;
  }
  
  try {
    const deviceFingerprint = await generateSimpleFingerprint();
    
    const { data, error } = await supabase.rpc('claim_welcome_bonus', {
      p_user_id: userId,
      p_device_fingerprint: deviceFingerprint,
      p_ip_address: null
    });
    
    if (error) {
      console.warn('Welcome bonus claim error:', error);
      return;
    }
    
    const rows = Array.isArray(data) ? data : [data];
    const result = rows[0];
    
    if (result?.success) {
       welcomeBonusClaimed.add(userId);
     }
  } catch (e) {
    console.warn('Failed to claim welcome bonus:', e);
  }
}

async function generateSimpleFingerprint(): Promise<string> {
  try {
    const components: string[] = [];
    components.push(navigator.userAgent);
    components.push(navigator.language);
    components.push(String(window.screen.width));
    components.push(String(window.screen.height));
    components.push(String(window.screen.colorDepth));
    components.push(String(new Date().getTimezoneOffset()));
    if ((navigator as any).hardwareConcurrency) {
      components.push(String((navigator as any).hardwareConcurrency));
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('fingerprint', 2, 2);
      components.push(canvas.toDataURL().slice(0, 50));
    }
    const encoder = new TextEncoder();
    const data = encoder.encode(components.join('|'));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
  } catch {
    return 'unknown-' + Date.now().toString(36);
  }
}

async function applyPendingReferralCode(user: User, profile: UserProfile | null): Promise<UserProfile | null> {
  const referredByCode = user.user_metadata?.referred_by?.trim();

  if (!referredByCode || profile?.referred_by) {
    return profile;
  }

  try {
    const { referralService } = await import('../services/referralService');
    const result = await referralService.applyReferralCode(referredByCode);

    if (!result.success) {
      console.warn('Referral code application returned an error:', result.error);
    }

    const { data: refreshedProfile, error: refreshError } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT)
      .eq('id', user.id)
      .single();

    if (refreshError) {
      console.warn('Referral applied but failed to refresh profile:', refreshError);
      return profile;
    }

    return refreshedProfile ?? profile;
  } catch (e) {
    console.warn('Error applying referral code:', e);
    return profile;
  }
}

export function useUserProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!user) {
      setProfile(null)
      setLoading(false)
      return
    }

    // Check cache first
    const cached = profileCache.get(user.id);
    const now = Date.now();
    if (cached && (now - cached.timestamp) < CACHE_DURATION) {
      setProfile(cached.data);
      setLoading(false);
      return;
    }

    // Debounce multiple rapid calls
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      const fetchOrCreateProfile = async () => {
        try {
          setLoading(true)
          setError(null)
          
          
          // First try to fetch existing profile
          const { data, error } = await supabase
            .from('profiles')
            .select(PROFILE_SELECT)
            .eq('id', user.id)
            .maybeSingle()

          if (error) {
            console.error('Error fetching profile:', error)
            setError(`Failed to fetch profile: ${error.message}`)
            return
          }

          if (data) {
            // Profile exists
            const updatedProfile = await applyPendingReferralCode(user, data);
            const finalProfile = updatedProfile ?? data;

            profileCache.set(user.id, { data: finalProfile, timestamp: Date.now() });
            setProfile(finalProfile)
          } else {
            // Profile doesn't exist, create one (new user)
            const newProfile = {
              id: user.id,
              name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              business_name: user.user_metadata?.business_name || null,
              phone: user.user_metadata?.phone || null,
              is_admin: false,
            }

            const { data: createdProfile, error: createError } = await supabase
              .from('profiles')
              .insert([newProfile])
              .select()
              .single()

            if (createError) {
              console.error('Error creating profile:', createError)
              setError(`Failed to create profile: ${createError.message}`)
            } else {
              const updatedProfile = await applyPendingReferralCode(user, createdProfile);
              const finalProfile = updatedProfile ?? createdProfile;

              profileCache.set(user.id, { data: finalProfile, timestamp: Date.now() });
              setProfile(finalProfile)
              claimWelcomeBonusForNewUser(user.id);
            }
          }
        } catch (err) {
          console.error('Profile fetch/create error:', err)
          setError('Failed to fetch or create profile')
        } finally {
          setLoading(false)
        }
      }

      fetchOrCreateProfile()
    }, 200); // 200ms debounce
  }, [user])

  return { 
    profile, 
    loading, 
    error, 
    refetch: () => {
      if (user) {
        // Clear cache to force fresh fetch
        profileCache.delete(user.id);
        
        const fetchOrCreateProfile = async () => {
          try {
            setLoading(true)
            setError(null)
            
            
            // First try to fetch existing profile
            const { data, error } = await supabase
              .from('profiles')
              .select(PROFILE_SELECT)
              .eq('id', user.id)
              .maybeSingle()

            if (error) {
              console.error('Error refetching profile:', error)
              setError(`Failed to fetch profile: ${error.message}`)
              return
            }

            if (data) {
              const updatedProfile = await applyPendingReferralCode(user, data);
              const finalProfile = updatedProfile ?? data;

              profileCache.set(user.id, { data: finalProfile, timestamp: Date.now() });
              setProfile(finalProfile)
            } else {
              const newProfile = {
                id: user.id,
                name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
                email: user.email || '',
                business_name: user.user_metadata?.business_name || null,
                phone: user.user_metadata?.phone || null,
                is_admin: false,
              }

              const { data: createdProfile, error: createError } = await supabase
                .from('profiles')
                .insert([newProfile])
                .select()
                .single()

              if (createError) {
                console.error('Error creating profile during refetch:', createError)
                setError(`Failed to create profile: ${createError.message}`)
              } else {
                const updatedProfile = await applyPendingReferralCode(user, createdProfile);
                const finalProfile = updatedProfile ?? createdProfile;

                profileCache.set(user.id, { data: finalProfile, timestamp: Date.now() });
                setProfile(finalProfile)
                claimWelcomeBonusForNewUser(user.id);
              }
            }
          } catch (err) {
            console.error('Profile refetch error:', err)
            setError('Failed to fetch or create profile')
          } finally {
            setLoading(false)
          }
        }
        fetchOrCreateProfile()
      }
    }
  }
}
